import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import html2pdf from 'html2pdf.js';
import { getPaiements } from '../services/api';
import { notificationService } from '../utils/notifications';
import './ListeMontantsPrint.css';

const ListePaiementsPrint = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const printRef = useRef(null);
  const [paiements, setPaiements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAssociation, setSelectedAssociation] = useState('');

  // Récupérer les paramètres depuis l'URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const search = params.get('search') || '';
    const association = params.get('association') || '';
    setSearchTerm(search);
    setSelectedAssociation(association);
  }, [location]);

  useEffect(() => {
    const loadPaiements = async () => {
      try {
        const response = await getPaiements();
        setPaiements(response.data || []);
      } catch (error) {
        console.error('Erreur lors du chargement des paiements:', error);
        notificationService.error('Erreur', 'Impossible de charger les paiements');
      } finally {
        setLoading(false);
      }
    };

    loadPaiements();
  }, []);

  const handlePrint = () => {
    setTimeout(() => {
      window.print();
    }, 100);
  };

  const handleDownloadPDF = async () => {
    if (!printRef.current) return;

    try {
      const opt = {
        margin: [10, 10, 10, 10],
        filename: `Liste_Paiements${selectedAssociation ? `_${selectedAssociation.replace(/\s+/g, '_')}` : ''}_${new Date().toISOString().split('T')[0]}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { 
          scale: 2,
          useCORS: true,
          logging: false,
          backgroundColor: '#ffffff',
          width: printRef.current.scrollWidth,
          height: printRef.current.scrollHeight,
          windowWidth: printRef.current.scrollWidth,
          allowTaint: true
        },
        jsPDF: { 
          unit: 'mm', 
          format: 'a4', 
          orientation: 'landscape'
        },
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'], avoid: ['.print-header', '.print-table'] }
      };

      await html2pdf().set(opt).from(printRef.current).save();
      notificationService.success('PDF généré', 'Le fichier PDF a été téléchargé avec succès.');
    } catch (error) {
      console.error('Erreur lors de la génération du PDF:', error);
      notificationService.error('Erreur', 'Erreur lors de la génération du PDF. Veuillez réessayer.');
    }
  };

  const handleClose = () => {
    window.close();
    if (!document.hidden) {
      navigate('/admin');
    }
  };

  // Filtrer les paiements
  const filteredPaiements = paiements.filter(paiement => {
    // Filtre par association
    if (selectedAssociation && paiement.membre?.association !== selectedAssociation) {
      return false;
    }
    
    // Filtre par recherche
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    const typePaiement = paiement.type_paiement === 'cotisation_initial' ? 'cotisation initiale' :
                      paiement.type_paiement === 'cotisation_mensuelle' ? 'cotisation mensuelle' :
                      paiement.type_paiement === 'autre' ? 'frais de carte' : '';
    const semaineText = paiement.semaine ? `semaine ${paiement.semaine}` : '';
    return (
      paiement.membre?.numero_matricule?.toLowerCase().includes(search) ||
      paiement.membre?.nom?.toLowerCase().includes(search) ||
      paiement.membre?.prenom?.toLowerCase().includes(search) ||
      paiement.date_paiement?.toLowerCase().includes(search) ||
      paiement.montant?.toString().includes(search) ||
      typePaiement.includes(search) ||
      semaineText.includes(search) ||
      paiement.numero_recu?.toLowerCase().includes(search)
    );
  });

  // Trier les paiements
  const sortedPaiements = [...filteredPaiements].sort((a, b) => {
    // Trier par membre d'abord
    const nomA = `${a.membre?.nom || ''} ${a.membre?.prenom || ''}`.trim();
    const nomB = `${b.membre?.nom || ''} ${b.membre?.prenom || ''}`.trim();
    if (nomA !== nomB) return nomA.localeCompare(nomB);
    
    // Puis par date de paiement (plus récent en premier)
    const dateA = new Date(a.date_paiement || 0);
    const dateB = new Date(b.date_paiement || 0);
    return dateB - dateA;
  });

  // Calculer les statistiques
  const totalPaiements = filteredPaiements.length;
  const totalMontant = filteredPaiements.reduce((sum, p) => sum + parseFloat(p.montant || 0), 0);
  const paiementsCotisationInitiale = filteredPaiements.filter(p => p.type_paiement === 'cotisation_initial').length;
  const paiementsCotisationMensuelle = filteredPaiements.filter(p => p.type_paiement === 'cotisation_mensuelle').length;
  const paiementsAutre = filteredPaiements.filter(p => p.type_paiement === 'autre').length;

  const getTypePaiementLabel = (type) => {
    switch(type) {
      case 'cotisation_initial':
        return 'Cotisation initiale';
      case 'cotisation_mensuelle':
        return 'Cotisation mensuelle';
      case 'autre':
        return 'Frais de carte';
      default:
        return type || '-';
    }
  };

  // Regrouper par membre (une ligne par membre)
  const groupedPaiementsByMember = useMemo(() => {
    const map = new Map();
    sortedPaiements.forEach(p => {
      const id = p.membre?.id ?? p.membre_id ?? 'unknown';
      if (!map.has(id)) {
        map.set(id, { membre: p.membre, paiements: [] });
      }
      map.get(id).paiements.push(p);
    });
    return Array.from(map.values());
  }, [sortedPaiements]);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Chargement des paiements...</p>
      </div>
    );
  }

  return (
    <div className="liste-montants-print-page" style={{ display: 'block', visibility: 'visible' }}>
      {/* Barre d'outils - masquée à l'impression */}
      <div className="print-toolbar" style={{ display: 'flex' }}>
        <div className="toolbar-left">
          <h2>Liste des Paiements - Impression{selectedAssociation ? ` - ${selectedAssociation}` : ''}</h2>
        </div>
        <div className="toolbar-right">
          <button className="btn btn-primary" onClick={handlePrint}>
            🖨️ Imprimer
          </button>
          <button className="btn btn-success" onClick={handleDownloadPDF}>
            📥 Télécharger PDF
          </button>
          <button className="btn btn-secondary" onClick={handleClose}>
            ✕ Fermer
          </button>
        </div>
      </div>

      {/* Contenu à imprimer */}
      <div ref={printRef} className="print-content" style={{ display: 'block', visibility: 'visible', background: 'white' }}>
        {/* En-tête */}
        <div className="print-header">
          <div className="header-content-wrapper">
            <div className="header-logo">
              <img src="/logo.png" alt="Logo" className="logo-img" />
            </div>
            <div className="header-text">
              <h1>MUTU NDE ESPOIR ASBL</h1>
              <h2>Liste des Paiements{selectedAssociation ? ` - ${selectedAssociation}` : ''}</h2>
              <div className="header-info">
                <p className="print-date">
                  <strong>Date d'édition :</strong> {new Date().toLocaleDateString('fr-FR', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
                {selectedAssociation && (
                  <p className="print-date" style={{ marginTop: '5px' }}>
                    <strong>Association :</strong> {selectedAssociation}
                  </p>
                )}
                <p className="print-date" style={{ marginTop: '5px' }}>
                  <strong>Total :</strong> {totalPaiements} paiement(s) - Montant total: {totalMontant.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} CDF
                </p>
                <p className="print-date" style={{ marginTop: '5px' }}>
                  <strong>Par type :</strong> Initiale: {paiementsCotisationInitiale} - Mensuelle: {paiementsCotisationMensuelle} - Frais de carte: {paiementsAutre}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Tableau : une ligne par membre, paiements regroupés */}
        <div className="print-table-container">
          <table className="print-table">
            <thead>
              <tr>
                <th>N°</th>
                <th>Matricule</th>
                <th>Nom complet</th>
                <th>Nb paiements</th>
                <th>Détail (Date · N° reçu · Type · Montant)</th>
                <th>Total (CDF)</th>
              </tr>
            </thead>
            <tbody>
              {groupedPaiementsByMember.length === 0 ? (
                <tr>
                  <td colSpan="6" style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                    {searchTerm || selectedAssociation ? 'Aucun paiement trouvé' : 'Aucun paiement enregistré'}
                  </td>
                </tr>
              ) : (
                groupedPaiementsByMember.map(({ membre, paiements }, index) => {
                  const total = paiements.reduce((s, p) => s + parseFloat(p.montant || 0), 0);
                  const detail = paiements.map(p => `${p.date_paiement ? new Date(p.date_paiement).toLocaleDateString('fr-FR') : '-'} · ${p.numero_recu || '-'} · ${getTypePaiementLabel(p.type_paiement)} · ${parseFloat(p.montant || 0).toLocaleString('fr-FR', { maximumFractionDigits: 0 })} CDF`).join(' ; ');
                  return (
                    <tr key={membre?.id ?? index}>
                      <td>{index + 1}</td>
                      <td>{membre?.numero_matricule || '-'}</td>
                      <td>{membre?.nom || ''} {membre?.prenom || ''}</td>
                      <td>{paiements.length}</td>
                      <td style={{ fontSize: '0.9em', whiteSpace: 'normal' }}>{detail}</td>
                      <td className="text-right">{total.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pied de page */}
        <div className="print-footer">
          <p>Document généré par MUTU NDE ESPOIR ASBL - Système de gestion</p>
        </div>
      </div>
    </div>
  );
};

export default ListePaiementsPrint;

